import { useEffect, useRef } from "react";
import Draggable, { DraggableData, DraggableEvent } from "react-draggable";

import Paper, { PaperProps } from "@mui/material/Paper";

import { useAppDispatch, useAppSelector } from "app/hooks";

import clampPosition from "./clamp-position.util";
import {
  selectQuickLauncherPosition,
  setQuickLauncherPosition
} from "./quick-launcher.slice";

/** Class applied to the launcher's search row — the only place a drag can start. */
export const DRAG_HANDLE_CLASS = "quick-launcher-drag-handle";

const ORIGIN = { x: 0, y: 0 };

/**
 * Draggable Paper for the quick launcher.
 *
 * Defined at **module scope** on purpose. MUI compares `PaperComponent` by
 * referential identity, so a component created inside `QuickLauncher` would
 * unmount and remount the whole panel on every keystroke — losing input focus,
 * the list's scroll position and the drag offset. It reads its own state from
 * Redux rather than taking props, which is what lets it stay a stable
 * reference. (Same trap documented in `features/billing/CLAUDE.md`.)
 */
export default function QuickLauncherPaper(props: PaperProps) {
  const dispatch = useAppDispatch();
  const position = useAppSelector(selectQuickLauncherPosition);
  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = nodeRef.current;
    if (!node || !position) return;

    const rect = node.getBoundingClientRect();
    const clamped = clampPosition(position, rect, {
      width: window.innerWidth,
      height: window.innerHeight
    });
    if (clamped !== position) dispatch(setQuickLauncherPosition(clamped));
    // Mount only. Re-running whenever `position` changes would fight an
    // in-progress drag, and the offset only needs correcting when it is
    // restored into a window it was not saved in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStop = (_event: DraggableEvent, data: DraggableData) => {
    dispatch(setQuickLauncherPosition({ x: data.x, y: data.y }));
  };

  return (
    <Draggable
      nodeRef={nodeRef}
      handle={`.${DRAG_HANDLE_CLASS}`}
      // Typing and selecting text has to keep working inside the search field,
      // and a drag begun on a result row would swallow the click that runs it.
      cancel="input, textarea, [data-no-drag]"
      // The Dialog's container is viewport-sized, so this keeps a *drag* inside
      // the window. It does not constrain a restored offset — that is what the
      // clamp effect above is for.
      bounds="parent"
      position={position ?? ORIGIN}
      onStop={handleStop}
    >
      <Paper ref={nodeRef} {...props} />
    </Draggable>
  );
}
